import {
  effectivePermissions,
  effectiveViews,
  type AccessMatrix,
  type UserAccessOverride,
} from './matrix';
import { MENU_VIEW_KEYS, PERMISSIONS, type MenuViewKey, type Permission, type Role } from './role';

/**
 * Access per person, on top of access per role. Request of 13 September 2026,
 * section 2.
 *
 * The role matrix stays the rule for everybody; a person's record carries the
 * exceptions, a grant or a deny per permission and per screen, and deny wins.
 * The server resolves the result into `session.permissions` and
 * `session.views`, exactly as it resolved the role alone before, so nothing that
 * reads a session changes. These helpers are for the mock server, which stands
 * in for it, and for the record screen, which edits the exceptions.
 */

export const ACCESS_CHOICES = ['inherit', 'grant', 'deny'] as const;
export type AccessChoice = (typeof ACCESS_CHOICES)[number];

/** Why this exception exists, shown on the screen and in the log. */
export const USER_ACCESS_NOTE_MAX = 200;

/** The refusals, worded once for the mock server, the screen and the tests. The API sends the same. */
export const USER_ACCESS_REFUSAL = {
  forbidden: "Only management may change a person's access.",
  ownAccess: 'You cannot change your own access.',
  stale: "Somebody else changed this person's access while you were editing. Refresh and try again.",
  lastAdministrator: "That would remove the last administrator's access to Users.",
  grantedAndDenied: 'A permission cannot be granted and denied at once',
  screenGrantedAndDenied: 'A screen cannot be granted and denied at once',
} as const;

/** One person's record as the API serves it: the exceptions, why, and who last set them. */
export interface UserAccess extends UserAccessOverride {
  userId: string;
  note: string | null;
  updatedByUserName: string | null;
  updatedAt: string | null;
  rowVersion: string;
}

export const NO_OVERRIDE: UserAccessOverride = {
  grantedPermissions: [],
  grantedViews: [],
  deniedPermissions: [],
  deniedViews: [],
};

/** One row of the record's card: a permission or a screen. */
export type AccessKey = { kind: 'permission'; key: Permission } | { kind: 'view'; key: MenuViewKey };

/** Every row the card shows, generated rather than listed, so a permission added later appears with no edit. */
export const ACCESS_KEYS: readonly AccessKey[] = [
  ...PERMISSIONS.map((key) => ({ kind: 'permission' as const, key })),
  ...MENU_VIEW_KEYS.map((key) => ({ kind: 'view' as const, key })),
];

/** Whether a record holds any exception at all. No exceptions is the state of every person on day one. */
export function hasExceptions(override: UserAccessOverride | null): boolean {
  return (
    override !== null &&
    override.grantedPermissions.length +
      override.grantedViews.length +
      override.deniedPermissions.length +
      override.deniedViews.length >
      0
  );
}

function listsOf(override: UserAccessOverride, kind: AccessKey['kind']) {
  return kind === 'permission'
    ? { granted: override.grantedPermissions as readonly string[], denied: override.deniedPermissions as readonly string[] }
    : { granted: override.grantedViews as readonly string[], denied: override.deniedViews as readonly string[] };
}

/** Where one key stands on a record. Deny wins over a grant listed for the same key. */
export function accessChoice(override: UserAccessOverride | null, access: AccessKey): AccessChoice {
  if (override === null) return 'inherit';
  const { granted, denied } = listsOf(override, access.kind);
  if (denied.includes(access.key)) return 'deny';
  return granted.includes(access.key) ? 'grant' : 'inherit';
}

/** The record with one key moved to a choice, and out of the list it was in. */
export function withAccessChoice(
  override: UserAccessOverride,
  access: AccessKey,
  choice: AccessChoice,
): UserAccessOverride {
  const { granted, denied } = listsOf(override, access.kind);
  const without = (list: readonly string[]) => list.filter((key) => key !== access.key);
  const nextGranted = choice === 'grant' ? [...without(granted), access.key] : without(granted);
  const nextDenied = choice === 'deny' ? [...without(denied), access.key] : without(denied);

  return access.kind === 'permission'
    ? { ...override, grantedPermissions: nextGranted as Permission[], deniedPermissions: nextDenied as Permission[] }
    : { ...override, grantedViews: nextGranted as MenuViewKey[], deniedViews: nextDenied as MenuViewKey[] };
}

/** The same key granted and denied at once, named by the list the server names. Empty when there is none. */
export function accessOverrideProblems(
  override: UserAccessOverride,
): Partial<Record<'grantedPermissions' | 'grantedViews', string>> {
  const problems: Partial<Record<'grantedPermissions' | 'grantedViews', string>> = {};
  if (override.grantedPermissions.some((key) => override.deniedPermissions.includes(key))) {
    problems.grantedPermissions = USER_ACCESS_REFUSAL.grantedAndDenied;
  }
  if (override.grantedViews.some((key) => override.deniedViews.includes(key))) {
    problems.grantedViews = USER_ACCESS_REFUSAL.screenGrantedAndDenied;
  }
  return problems;
}

/**
 * Whether anybody active would still reach Users & permissions and change
 * anything there: the Users screen and the admin permission, both effective.
 * The role matrix locks the Users screen to Management so the lab cannot be
 * locked out with one checkbox; this is the same guard for one person at a time.
 */
export function keepsAnAdministrator(
  people: ReadonlyArray<{ role: Role; disabled: boolean; override: UserAccessOverride | null }>,
  matrix: AccessMatrix,
): boolean {
  return people.some(
    (person) =>
      !person.disabled &&
      effectiveViews(person.role, matrix, person.override).includes('users') &&
      effectivePermissions(person.role, matrix, person.override).includes('admin'),
  );
}

export interface AccessChange {
  access: AccessKey;
  from: AccessChoice;
  to: AccessChoice;
}

/** Every key whose choice moved, permissions first and then screens, in the card's order. */
export function accessChanges(
  before: UserAccessOverride | null,
  after: UserAccessOverride | null,
): AccessChange[] {
  return ACCESS_KEYS.flatMap((access) => {
    const from = accessChoice(before, access);
    const to = accessChoice(after, access);
    return from === to ? [] : [{ access, from, to }];
  });
}
